import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  ACTIVITY_TYPES,
  ACTIVITY_TYPE_LABEL,
  DEPARTMENT_LABEL,
  canOpenView,
  type ActivityEntry,
  type ActivityType,
  type Department,
  type ViewKey,
} from '@/domain';
import { useActivity, useUsers } from '@/application/usecases/useAdmin';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { useSession } from '@/application/usecases/useSession';
import { initials } from '@/shared/format/initials';
import { strings } from '@/shared/strings';
import { formatInstant } from '@/shared/format/instant';
import {
  ArchiveIcon,
  CameraIcon,
  CheckIcon,
  ClockIcon,
  FinalCheckIcon,
  FlagIcon,
  LockIcon,
  NoteIcon,
  PayIcon,
  PlayIcon,
  PlusIcon,
  PrintIcon,
  ReworkIcon,
  ShipIcon,
  SignOutIcon,
  StaffIcon,
} from '@/ui/icons';
import { Pagination } from '@/ui/data/Pagination';
import { SkeletonRows } from '@/ui/feedback/Loading';
import styles from './ActivityScreen.module.css';

/** Matches the archive's page size, and the prototype shows one page. */
const PAGE_SIZE = 80;

/**
 * Eighty stays the default here, as on the archive, and is offered beside the
 * four. Section 10.3 of the request of 16 September 2026.
 */
const ACTIVITY_PAGE_SIZES = [...PAGE_SIZES, PAGE_SIZE].sort((a, b) => a - b);

/**
 * The tint and the icon per activity type, from the prototype's `tint`, `tcol`
 * and `ic` maps. Colours come from the token set rather than from literals so
 * the two themes stay in step.
 */
const TONE: Record<ActivityType, string> = {
  scan: 'brand',
  done: 'brand',
  assign: 'brand',
  intake: 'brand',
  date: 'slate',
  ship: 'slate',
  hold: 'slate',
  urgent: 'late',
  qc: 'ok',
  pay: 'ok',
  rework: 'watch',
  note: 'watch',
  admin: 'gold',
  print: 'slate',
  // A remake is work to redo, the same tone as a rework.
  redo: 'watch',
  // Graphite, as a hold is: a deletion stops a case, and red means urgent.
  delete: 'slate',
  auth: 'slate',
  photo: 'brand',
  edit: 'watch',
  access: 'gold',
  // Graphite, as a deletion is: the case has stopped, and red means urgent.
  archive: 'slate',
};

const ICON: Record<ActivityType, ReactNode> = {
  scan: <PlayIcon />,
  done: <CheckIcon />,
  assign: <CheckIcon />,
  date: <ClockIcon />,
  urgent: <FlagIcon />,
  qc: <FinalCheckIcon />,
  pay: <PayIcon />,
  ship: <ShipIcon />,
  hold: <ClockIcon />,
  intake: <PlusIcon />,
  rework: <ReworkIcon />,
  note: <NoteIcon />,
  admin: <StaffIcon />,
  print: <PrintIcon />,
  redo: <ReworkIcon />,
  delete: <ArchiveIcon />,
  auth: <SignOutIcon />,
  photo: <CameraIcon />,
  edit: <NoteIcon />,
  access: <LockIcon />,
  archive: <ArchiveIcon />,
};

/** A complete job order number, the only thing worth sending as a case filter. */
const JOB_ORDER = /^JO-\d+$/;

/** Where a row's case opens, for a caller whose screens open it, or nowhere. */
function caseLink(views: readonly ViewKey[], jobOrderNo: string | null): string | null {
  if (jobOrderNo === null) return null;
  if (views.includes('orders')) return `/orders/${jobOrderNo}/record`;
  if (canOpenView(views, 'record')) return `/archive/${jobOrderNo}`;
  return null;
}

/**
 * Activity log. Sections 5.2 and 7.6.
 *
 * The audit trail, filterable by user and by activity type, newest first and
 * paginated. Both filters are applied by the server: the log is the one table
 * that grows on every mutation anywhere in the building, so a client-side
 * filter would search one page of it and call that an audit.
 *
 * There is no way to write from this screen and that is the point. Section 7.10
 * item 10 makes the table append only, written inside the same transaction as
 * the mutation it records.
 */
export function ActivityScreen() {
  const ids = { user: useId(), type: useId(), jobOrder: useId() };
  const [userId, setUserId] = useState('all');
  const [type, setType] = useState<ActivityType | 'all'>('all');
  const [caseTyped, setCaseTyped] = useState('');
  const session = useSession();
  const views = session.data?.views ?? [];

  // The case filter waits for a whole number: JO-20 is not a case, and asking
  // the server about it on every keystroke would be a query per character.
  const typed = caseTyped.trim().toUpperCase();
  const jobOrderNo = JOB_ORDER.test(typed) ? typed : '';

  /*
   * A page at a time, with the size the reader chose. Section 10 of the request
   * of 16 September 2026 replaces "showing the first 80, refine the search for
   * the rest", which told somebody the log was longer than the screen and then
   * left them no way to read the rest of it.
   */
  const paging = useTablePage('activity', PAGE_SIZE, ACTIVITY_PAGE_SIZES);

  const filter = useMemo(
    () => ({ userId, type, jobOrderNo, page: paging.page, pageSize: paging.pageSize }),
    [userId, type, jobOrderNo, paging.page, paging.pageSize],
  );
  const { data, isPending, isError } = useActivity(filter);
  const users = useUsers();

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  // A filter makes a different log, and page three of the old one is nothing in
  // the new one.
  const { reset } = paging;
  useEffect(() => {
    reset();
  }, [reset, userId, type, jobOrderNo]);

  // Grouped by department, as in the prototype. A log filtered by person is
  // usually filtered by "who on plaster", not by a name recalled from memory.
  const byDepartment = useMemo(() => {
    const groups = new Map<Department, { id: string; name: string }[]>();
    for (const user of users.data?.items ?? []) {
      const group = groups.get(user.department) ?? [];
      group.push({ id: user.id, name: user.name });
      groups.set(user.department, group);
    }
    return [...groups.entries()];
  }, [users.data]);

  // The entry carries the actor's name but not their department, and "who on
  // ceramic did this" is the question this screen is opened to answer.
  const departmentOf = useMemo(() => {
    const map = new Map<string, Department>();
    for (const user of users.data?.items ?? []) map.set(user.id, user.department);
    return map;
  }, [users.data]);

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.lbl}>{strings.activity.label}</span>
        <h2>{strings.activity.title}</h2>
        <span className={styles.hint}>{strings.activity.hint}</span>
      </div>

      <div className={styles.filters}>
        <div className={`${styles.field} ${styles.grow}`}>
          <label className={styles.l} htmlFor={ids.user}>
            {strings.activity.byUser}
          </label>
          <select
            id={ids.user}
            className={styles.select}
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
          >
            <option value="all">{strings.activity.allUsers}</option>
            {byDepartment.map(([department, members]) => (
              <optgroup key={department} label={DEPARTMENT_LABEL[department]}>
                {members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.type}>
            {strings.activity.type}
          </label>
          <select
            id={ids.type}
            className={styles.select}
            value={type}
            onChange={(event) => setType(event.target.value as ActivityType | 'all')}
          >
            <option value="all">{strings.activity.allTypes}</option>
            {ACTIVITY_TYPES.map((key) => (
              <option key={key} value={key}>
                {ACTIVITY_TYPE_LABEL[key]}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.jobOrder}>
            {strings.activity.byCase}
          </label>
          <input
            id={ids.jobOrder}
            className={styles.select}
            type="search"
            inputMode="text"
            autoComplete="off"
            placeholder={strings.activity.casePlaceholder}
            value={caseTyped}
            onChange={(event) => setCaseTyped(event.target.value)}
          />
        </div>
      </div>

      {isError && <p className={styles.hint}>{strings.errors.server}</p>}
      {isPending && <SkeletonRows rows={6} label={strings.common.loadingActivity} />}

      {!isError && !isPending && (
        <>
          {/* Every row the filter matches. The page itself is counted below. */}
          <div className={styles.count}>
            <b>{total.toLocaleString()}</b>{' '}
            {total === 1 ? strings.activity.countedOne : strings.activity.counted}
          </div>

          {rows.length === 0 ? (
            <div className={styles.empty}>
              <h3>{strings.activity.empty}</h3>
              <p>{strings.activity.emptyDetail}</p>
            </div>
          ) : (
            <ul className={styles.list}>
              {rows.map((entry) => (
                <Row
                  key={entry.id}
                  entry={entry}
                  department={
                    entry.byUserId === null ? undefined : departmentOf.get(entry.byUserId)
                  }
                  caseTo={caseLink(views, entry.jobOrderNo)}
                />
              ))}
            </ul>
          )}

          <Pagination
            page={paging.page}
            pageSize={paging.pageSize}
            total={total}
            sizes={ACTIVITY_PAGE_SIZES}
            onPage={paging.goTo}
            onPageSize={paging.choose}
            label={strings.table.pagesLabel}
          />
        </>
      )}
    </div>
  );
}

function Row({
  entry,
  department,
  caseTo,
}: {
  entry: ActivityEntry;
  department?: Department;
  /** The case the row belongs to, for a caller who can open it. */
  caseTo: string | null;
}) {
  // A row written by the system rather than by a person keeps its own label:
  // an empty avatar with no name reads as a bug.
  const name = entry.byUserName ?? strings.activity.system;

  return (
    <li className={styles.row}>
      <span className={`${styles.icon} ${styles[TONE[entry.type]] ?? ''}`} aria-hidden="true">
        {ICON[entry.type]}
      </span>
      <span className={styles.avatar} aria-hidden="true">
        {entry.byUserName === null ? '·' : initials(entry.byUserName)}
      </span>
      <div className={styles.body}>
        <div className={styles.line}>
          <b>{name}</b> {entry.action}{' '}
          {entry.target !== '' &&
            (caseTo === null ? (
              <span className={styles.target}>{entry.target}</span>
            ) : (
              <Link className={`${styles.target} ${styles.caseLink}`} to={caseTo}>
                {entry.target}
              </Link>
            ))}
        </div>
        <div className={styles.sub}>
          {department === undefined
            ? ACTIVITY_TYPE_LABEL[entry.type]
            : DEPARTMENT_LABEL[department]}
        </div>
      </div>
      <time className={styles.when} dateTime={entry.createdAt}>
        {formatInstant(entry.createdAt)}
      </time>
    </li>
  );
}
